/**
 * 替代品规则新增/编辑 Drawer 表单组件
 */

import React, { useEffect, useState, useCallback } from 'react';
import {
  Drawer,
  Form,
  Button,
  Input,
  Select,
  InputNumber,
  DatePicker,
  message,
  Divider,
  Tooltip,
} from 'antd';
import { PlusOutlined, DeleteOutlined, ShopOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import type { SubstituteRuleDTO, CreateSubstituteRuleRequest, UpdateSubstituteRuleRequest, SubstituteMatchConfig } from '../../../types/promotion-rule/substituteRule';
import { MatchType, MatchTypeOptions, MatchRule, MatchRuleOptions, CategoryLevel, CategoryLevelOptions } from '../../../types/promotion-rule/substituteRule';
import type { Store } from '../../../types/master-data/store';
import * as substituteRuleService from '../../../services/promotion-rule/substituteRuleService';
import BatchAddStoreModal from './BatchAddStoreModal';

const { TextArea } = Input;
const { RangePicker } = DatePicker;

// 根据匹配类型获取可用的匹配规则
const getAvailableMatchRules = (matchType: MatchType): { label: string; value: MatchRule }[] => {
  switch (matchType) {
    case MatchType.PRICE:
      return [
        { label: 'promotionRule.form.matchRule.greaterThan', value: MatchRule.GREATER_THAN },
        { label: 'promotionRule.form.matchRule.greaterThanOrEqual', value: MatchRule.GREATER_THAN_OR_EQUAL },
        { label: 'promotionRule.form.matchRule.lessThan', value: MatchRule.LESS_THAN },
        { label: 'promotionRule.form.matchRule.lessThanOrEqual', value: MatchRule.LESS_THAN_OR_EQUAL },
        { label: 'promotionRule.form.matchRule.equal', value: MatchRule.EQUAL },
      ];
    case MatchType.CATEGORY:
    case MatchType.BRAND:
      return [
        { label: 'promotionRule.form.matchRule.mustMatch', value: MatchRule.MUST_MATCH },
      ];
    default:
      return MatchRuleOptions;
  }
};

interface SubstituteRuleDrawerProps {
  visible: boolean;
  mode: 'create' | 'edit';
  record: SubstituteRuleDTO | null;
  onClose: () => void;
  onSuccess: () => void;
}

const SubstituteRuleDrawer: React.FC<SubstituteRuleDrawerProps> = ({
  visible,
  mode,
  record,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [matchConfigs, setMatchConfigs] = useState<SubstituteMatchConfig[]>([]);

  // 门店选择状态
  const [selectedStores, setSelectedStores] = useState<Store[]>([]);
  const [batchAddModalVisible, setBatchAddModalVisible] = useState(false);

  // 初始化表单数据
  useEffect(() => {
    if (visible) {
      if (mode === 'edit' && record) {
        form.setFieldsValue({
          ruleName: record.ruleName,
          priority: record.priority,
          effectiveTime: record.effectiveEndTime
            ? [dayjs(record.effectiveStartTime), dayjs(record.effectiveEndTime)]
            : [dayjs(record.effectiveStartTime), null],
          description: record.description,
          applyStoreTypes: record.applyStoreTypes || [],
          excludeStores: record.excludeStores || [],
        });
        // 设置匹配配置数据（Service 层已完成 JSON parse）
        const configs: SubstituteMatchConfig[] = record.matchConfigs?.map((c, index) => ({
          id: `config-${index}-${Date.now()}`,
          matchType: c.matchType,
          matchRule: c.matchRule,
          priority: c.priority || `L${index + 1}`,
          ...(c.matchType === MatchType.CATEGORY ? { categoryLevel: c.categoryLevel || CategoryLevel.L2 } : {}),
        })) || [];
        setMatchConfigs(configs);
        // 设置已选门店（applyStoreCodes 已由 Service 层 parse 为数组）
        const storeCodes = record.applyStoreCodes || [];
        setSelectedStores(storeCodes.map(code => ({ storeCode: code } as Store)));
      } else {
        form.resetFields();
        form.setFieldsValue({
          priority: 1,
          effectiveTime: [dayjs(), null],
        });
        setMatchConfigs([]);
        setSelectedStores([]);
      }
    }
  }, [visible, mode, record, form]);

  // 添加匹配配置行
  const handleAddConfig = () => {
    const newConfig: SubstituteMatchConfig = {
      id: `config-${Date.now()}`,
      matchType: MatchType.PRICE,
      matchRule: MatchRule.EQUAL,
      priority: `L${matchConfigs.length + 1}`,
    };
    setMatchConfigs([...matchConfigs, newConfig]);
  };

  // 删除匹配配置行
  const handleDeleteConfig = (id: string) => {
    const newData = matchConfigs.filter(item => item.id !== id);
    // 重新计算优先级
    const updatedData = newData.map((item, index) => ({
      ...item,
      priority: `L${index + 1}`,
    }));
    setMatchConfigs(updatedData);
  };

  // 更新匹配配置数据
  const handleUpdateConfig = (id: string, field: keyof SubstituteMatchConfig, value: any) => {
    const newData = matchConfigs.map(item => {
      if (item.id === id) {
        const updated = { ...item, [field]: value };
        // 如果修改了匹配类型，重置匹配规则
        if (field === 'matchType') {
          updated.matchRule = getAvailableMatchRules(value as MatchType)[0]?.value || MatchRule.MUST_MATCH;
          // 切换到 CATEGORY 时默认设置 L2，切换到其他类型时清除 categoryLevel
          if (value === MatchType.CATEGORY) {
            updated.categoryLevel = CategoryLevel.L2;
          } else {
            updated.categoryLevel = undefined;
          }
        }
        return updated;
      }
      return item;
    });
    setMatchConfigs(newData);
  };

  // 处理门店选择确认
  const handleBatchAddStoreConfirm = (stores: Store[], applyStoreTypes: string[], excludeStores: string[]) => {
    // 更新表单值
    form.setFieldsValue({
      applyStoreTypes,
      excludeStores,
    });

    // 直接替换为新的门店列表
    setSelectedStores(stores);
    setBatchAddModalVisible(false);
  };

  // 验证匹配配置数据
  const validateMatchConfigs = (): string | null => {
    if (matchConfigs.length === 0) {
      return t('promotionRule.form.rules.configRequired');
    }

    // 检查是否有重复的匹配类型
    const typeSet = new Set<MatchType>();
    for (const config of matchConfigs) {
      if (typeSet.has(config.matchType)) {
        return t('promotionRule.form.rules.duplicateMatchType');
      }
      typeSet.add(config.matchType);
    }

    // 检查是否有重复的优先级
    const prioritySet = new Set<string>();
    for (const config of matchConfigs) {
      const priority = config.priority || `L${matchConfigs.indexOf(config) + 1}`;
      if (prioritySet.has(priority)) {
        return t('promotionRule.form.rules.duplicatePriority');
      }
      prioritySet.add(priority);
    }

    return null;
  };

  // 提交表单
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      // 验证匹配配置
      const validationError = validateMatchConfigs();
      if (validationError) {
        message.error(validationError);
        return;
      }

      setLoading(true);

      const effectiveStartTime = values.effectiveTime?.[0]?.valueOf() || Date.now();
      const effectiveEndTime = values.effectiveTime?.[1]?.valueOf() || null;

      // 构建匹配配置数据
      const matchConfigsData = matchConfigs.map(c => ({
        matchType: c.matchType,
        matchRule: c.matchRule,
        priority: c.priority,
        ...(c.matchType === MatchType.CATEGORY && c.categoryLevel ? { categoryLevel: c.categoryLevel } : {}),
      }));

      if (mode === 'create') {
        const createData: CreateSubstituteRuleRequest = {
          ruleName: values.ruleName,
          description: values.description,
          priority: values.priority,
          effectiveStartTime,
          effectiveEndTime,
          applyStoreCodes: selectedStores.map((store) => store.storeCode),
          applyStoreTypes: form.getFieldValue('applyStoreTypes') || [],
          excludeStores: form.getFieldValue('excludeStores') || [],
          matchConfigs: matchConfigsData,
        };

        const response = await substituteRuleService.createSubstituteRule(createData);
        if (response.success) {
          message.success(t('promotionRule.message.createSuccess'));
          onSuccess();
        } else {
          message.error(response.errorMessage || t('common.message.operationFailed'));
        }
      } else if (record) {
        const updateData: UpdateSubstituteRuleRequest = {
          ruleCode: record.ruleCode,
          ruleName: values.ruleName,
          description: values.description,
          priority: values.priority,
          effectiveStartTime,
          effectiveEndTime,
          applyStoreCodes: selectedStores.map((store) => store.storeCode),
          applyStoreTypes: form.getFieldValue('applyStoreTypes') || [],
          excludeStores: form.getFieldValue('excludeStores') || [],
          matchConfigs: matchConfigsData,
        };

        const response = await substituteRuleService.updateSubstituteRule(updateData);
        if (response.success) {
          message.success(t('promotionRule.message.updateSuccess'));
          onSuccess();
        } else {
          message.error(response.errorMessage || t('common.message.operationFailed'));
        }
      }
    } catch (error) {
      console.error('Form validation failed:', error);
    } finally {
      setLoading(false);
    }
  };

  // 渲染匹配配置表格行
  const renderConfigRow = (config: SubstituteMatchConfig, index: number) => {
    const availableRules = getAvailableMatchRules(config.matchType);

    return (
      <div
        key={config.id}
        style={{
          display: 'flex',
          gap: 8,
          alignItems: 'center',
          padding: '12px 0',
          borderBottom: index < matchConfigs.length - 1 ? '1px solid #f0f0f0' : 'none',
        }}
      >
        {/* 匹配类型 */}
        <div style={{ flex: '0 0 140px' }}>
          <Select
            key={`type-${config.id}`}
            value={config.matchType}
            onChange={(value) => handleUpdateConfig(config.id, 'matchType', value)}
            style={{ width: '100%' }}
            placeholder={t('promotionRule.form.matchConfig.selectMatchType')}
          >
            {MatchTypeOptions.map(opt => (
              <Select.Option key={opt.value} value={opt.value}>
                {t(opt.label)}
              </Select.Option>
            ))}
          </Select>
        </div>

        {/* 匹配规则 */}
        <div style={{ flex: '0 0 140px' }}>
          <Select
            key={`rule-${config.id}`}
            value={config.matchRule}
            onChange={(value) => handleUpdateConfig(config.id, 'matchRule', value)}
            style={{ width: '100%' }}
            placeholder={t('promotionRule.form.matchConfig.selectMatchRule')}
          >
            {availableRules.map(opt => (
              <Select.Option key={opt.value} value={opt.value}>
                {t(opt.label)}
              </Select.Option>
            ))}
          </Select>
        </div>

        {/* 品类层级（仅 Category 类型显示） */}
        <div style={{ flex: '0 0 130px' }}>
          {config.matchType === MatchType.CATEGORY ? (
            <Select
              key={`catLevel-${config.id}`}
              value={config.categoryLevel || CategoryLevel.L2}
              onChange={(value) => handleUpdateConfig(config.id, 'categoryLevel', value)}
              style={{ width: '100%' }}
              placeholder={t('promotionRule.form.matchConfig.selectCategoryLevel')}
            >
              {CategoryLevelOptions.map(opt => (
                <Select.Option key={opt.value} value={opt.value}>
                  {t(opt.label)}
                </Select.Option>
              ))}
            </Select>
          ) : (
            <span style={{ color: '#bfbfbf', fontSize: 13 }}>—</span>
          )}
        </div>

        {/* 优先级 */}
        <div style={{ flex: '0 0 100px' }}>
          <Select
            value={config.priority || `L${index + 1}`}
            onChange={(value) => handleUpdateConfig(config.id, 'priority', value)}
            style={{ width: '100%' }}
            placeholder={t('promotionRule.form.matchConfig.selectPriority')}
          >
            {['L1', 'L2', 'L3', 'L4', 'L5', 'L6', 'L7'].map(level => (
              <Select.Option key={level} value={level}>
                {level}
              </Select.Option>
            ))}
          </Select>
        </div>

        {/* 操作按钮 - 只保留删除 */}
        <div style={{ flex: '0 0 40px', display: 'flex', justifyContent: 'center' }}>
          <Tooltip title={t('promotionRule.form.matchConfig.delete')}>
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDeleteConfig(config.id)}
            />
          </Tooltip>
        </div>
      </div>
    );
  };

  return (
    <>
      <Drawer
        title={mode === 'create' ? t('promotionRule.form.title.create') : t('promotionRule.form.title.edit')}
        placement="right"
        width={720}
        onClose={onClose}
        open={visible}
        destroyOnClose
        footer={
          <div style={{ textAlign: 'right' }}>
            <Button onClick={onClose} style={{ marginRight: 8 }}>
              {t('common.actions.cancel')}
            </Button>
            <Button type="primary" onClick={handleSubmit} loading={loading}>
              {t('common.actions.save')}
            </Button>
          </div>
        }
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            effectiveTime: [dayjs(), null],
          }}
        >
          <Form.Item
            name="ruleName"
            label={t('promotionRule.form.fields.ruleName')}
            rules={[{ required: true, message: t('promotionRule.form.rules.ruleNameRequired') }]}
          >
            <Input placeholder={t('promotionRule.form.placeholder.ruleName')} maxLength={100} />
          </Form.Item>

          <Form.Item
            name="effectiveTime"
            label={t('promotionRule.form.fields.effectiveStartTime')}
            rules={[{ required: true, message: t('promotionRule.form.rules.effectiveStartTimeRequired') }]}
          >
            <RangePicker
              showTime
              format="DD-MM-YYYY HH:mm:ss"
              style={{ width: '100%' }}
              placeholder={[
                t('promotionRule.form.placeholder.effectiveStartTime'),
                t('promotionRule.form.placeholder.effectiveEndTime'),
              ]}
            />
          </Form.Item>

          <Form.Item
            name="description"
            label={t('promotionRule.form.fields.description')}
          >
            <TextArea
              placeholder={t('promotionRule.form.placeholder.description')}
              maxLength={500}
              rows={3}
            />
          </Form.Item>

          {/* 应用门店 */}
          <Form.Item label={t('promotionRule.form.fields.applyStores')}>
            <Button
              icon={<ShopOutlined />}
              onClick={() => setBatchAddModalVisible(true)}
            >
              {t('promotionRule.form.storeSelect.selectStore')}
            </Button>
          </Form.Item>

          <Divider />

          {/* 匹配配置 */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontWeight: 500, marginBottom: 12 }}>{t('promotionRule.form.matchConfig.title')}</div>
            
            {/* 表头 */}
            <div
              style={{
                display: 'flex',
                gap: 8,
                alignItems: 'center',
                padding: '8px 0',
                borderBottom: '1px solid #d9d9d9',
                background: '#fafafa',
                fontWeight: 500,
                fontSize: 14,
              }}
            >
              <div style={{ flex: '0 0 140px' }}>{t('promotionRule.form.matchConfig.matchType')}</div>
              <div style={{ flex: '0 0 140px' }}>{t('promotionRule.form.matchConfig.matchRule')}</div>
              <div style={{ flex: '0 0 130px' }}>{t('promotionRule.form.matchConfig.categoryLevel')}</div>
              <div style={{ flex: '0 0 100px' }}>{t('promotionRule.form.matchConfig.priority')}</div>
              <div style={{ flex: '0 0 40px', textAlign: 'center' }}>{t('promotionRule.form.matchConfig.actions')}</div>
            </div>

            {/* 配置行 */}
            {matchConfigs.length === 0 ? (
              <div
                style={{
                  padding: '24px 0',
                  textAlign: 'center',
                  color: '#999',
                  borderBottom: '1px solid #f0f0f0',
                }}
              >
                {t('promotionRule.form.matchConfig.emptyText')}
              </div>
            ) : (
              matchConfigs.map((config, index) => renderConfigRow(config, index))
            )}

            {/* 添加按钮 */}
            <div style={{ marginTop: 12, textAlign: 'center' }}>
              <Button type="dashed" icon={<PlusOutlined />} onClick={handleAddConfig}>
                {t('promotionRule.form.matchConfig.addRow')}
              </Button>
            </div>
          </div>
        </Form>
      </Drawer>

      {/* 门店选择 Drawer */}
      <BatchAddStoreModal
        visible={batchAddModalVisible}
        onCancel={() => setBatchAddModalVisible(false)}
        onConfirm={handleBatchAddStoreConfirm}
        existingStoreCodes={[]}
        initialSelectedStoreCodes={selectedStores.map(s => s.storeCode)}
        initialApplyStoreTypes={form.getFieldValue('applyStoreTypes') || []}
        initialExcludeStores={form.getFieldValue('excludeStores') || []}
      />
    </>
  );
};

export default SubstituteRuleDrawer;
